import html2pdf from "html2pdf.js";
import { formatAppDate } from "./dateUtils.js";

/**
 * Generates an A4 PDF Blob for an invoice from printable DOM pages
 */
export async function generateInvoicePdfBlob(invoiceElement, invoice) {
  if (!invoiceElement) {
    throw new Error("Printable invoice element not found");
  }

  const invoiceNo = invoice.invoiceNo || "bill";
  const filename = `Invoice_${invoiceNo}.pdf`;

  // 1. Create a clean export container without dark UI wrappers or negative z-index
  const exportContainer = document.createElement("div");
  exportContainer.className = "pdf-export-container";
  exportContainer.style.background = "#ffffff";
  exportContainer.style.color = "#000000";
  exportContainer.style.width = "794px";
  exportContainer.style.margin = "0";
  exportContainer.style.padding = "0";
  exportContainer.style.boxSizing = "border-box";

  // 2. Clone each printable page
  const pages = invoiceElement.querySelectorAll(".printable-page");
  if (pages.length > 0) {
    pages.forEach((page, idx) => {
      const pageClone = page.cloneNode(true);
      pageClone.style.width = "100%";
      pageClone.style.maxWidth = "100%";
      pageClone.style.minWidth = "0";
      pageClone.style.margin = "0";
      pageClone.style.padding = "10mm 10mm 10mm 10mm";
      pageClone.style.boxSizing = "border-box";
      pageClone.style.boxShadow = "none";
      pageClone.style.border = "none";
      pageClone.style.background = "#ffffff";
      if (idx > 0) {
        pageClone.style.pageBreakBefore = "always";
        pageClone.style.breakBefore = "page";
      }
      const mtContainer = pageClone.querySelector(".modern-tax-container");
      if (mtContainer) {
        mtContainer.style.padding = "0";
      }
      exportContainer.appendChild(pageClone);
    });
  } else {
    const clone = invoiceElement.cloneNode(true);
    clone.style.width = "100%";
    clone.style.maxWidth = "100%";
    clone.style.minWidth = "0";
    clone.style.margin = "0";
    clone.style.padding = "10mm 10mm 10mm 10mm";
    clone.style.boxSizing = "border-box";
    clone.style.background = "#ffffff";
    clone.style.boxShadow = "none";
    clone.style.border = "none";
    const mtContainer = clone.querySelector(".modern-tax-container");
    if (mtContainer) {
      mtContainer.style.padding = "0";
    }
    exportContainer.appendChild(clone);
  }

  const opt = {
    margin: 0,
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] }
  };

  const pdfBlob = await html2pdf().set(opt).from(exportContainer).outputPdf("blob");
  window.__lastPdfBlob = pdfBlob;
  return { pdfBlob, filename };
}

/**
 * Downloads the Invoice PDF directly to the user's device
 */
export async function downloadInvoicePdf(invoiceElement, invoice) {
  const { pdfBlob, filename } = await generateInvoicePdfBlob(invoiceElement, invoice);
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
  return { filename, size: pdfBlob.size };
}

/**
 * Generates an A4 PDF Blob from a printable DOM element and shares it
 * directly to WhatsApp using the Web Share API (Option A).
 * If on desktop where file sharing isn't supported, downloads the PDF
 * and opens WhatsApp chat as a fallback.
 */
export async function shareInvoicePdf(invoiceElement, invoice, issuingName = "SREE RAM TRADERS") {
  const { pdfBlob, filename } = await generateInvoicePdfBlob(invoiceElement, invoice);
  const invoiceNo = invoice.invoiceNo || "bill";
  const file = new File([pdfBlob], filename, { type: "application/pdf" });

  const companyDateFormat = invoice.companyDetails?.dateFormat || invoice.sellerConfig?.dateFormat || "DD-MM-YYYY";
  const formattedDate = formatAppDate(invoice.invoiceDate, companyDateFormat);
  const shareText = `*${issuingName}*\nInvoice #${invoiceNo} (${formattedDate})\nTotal: ₹${Number(invoice.grandTotal || 0).toLocaleString("en-IN")}\nThank you for your business!`;

  // Option A: Native Web Share API with Files (Android, iOS, iPadOS, Safari, supported PWA)
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `Invoice #${invoiceNo}`,
        text: shareText
      });
      return { sharedDirectly: true };
    } catch (shareErr) {
      if (shareErr.name === "AbortError") {
        return { aborted: true };
      }
      console.warn("Native share failed, falling back to download & WhatsApp Web:", shareErr);
    }
  }

  // Fallback for Desktop browsers without native file share sheet:
  // Automatically trigger download of the PDF and open WhatsApp Web
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);

  const rawPhone = (invoice.customerPhone || "").replace(/\D/g, "");
  const targetPhone = rawPhone ? `91${rawPhone.slice(-10)}` : "";
  const waUrl = targetPhone
    ? `https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(shareText + "\n(PDF downloaded to your device)")}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + "\n(PDF downloaded to your device)")}`;

  window.open(waUrl, "_blank");
  return { sharedDirectly: false, downloaded: true };
}

/**
 * Generates an A4 PDF Blob for a Payment Receipt
 */
export async function generateReceiptPdfBlob(receiptElement, receipt) {
  if (!receiptElement) {
    throw new Error("Printable receipt element not found");
  }

  const receiptNo = receipt.paymentNo || "Receipt";
  const filename = `PaymentReceipt_${receiptNo}.pdf`;

  const exportContainer = document.createElement("div");
  exportContainer.className = "pdf-export-receipt-container";
  exportContainer.style.background = "#ffffff";
  exportContainer.style.color = "#000000";
  exportContainer.style.width = "794px";
  exportContainer.style.margin = "0";
  exportContainer.style.padding = "0";
  exportContainer.style.boxSizing = "border-box";

  const clone = receiptElement.cloneNode(true);
  clone.style.width = "100%";
  clone.style.maxWidth = "100%";
  clone.style.margin = "0";
  clone.style.padding = "10mm 10mm 10mm 10mm";
  clone.style.boxSizing = "border-box";
  clone.style.background = "#ffffff";
  clone.style.boxShadow = "none";
  clone.style.border = "none";

  exportContainer.appendChild(clone);

  const opt = {
    margin: 0,
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
  };

  const pdfBlob = await html2pdf().set(opt).from(exportContainer).outputPdf("blob");
  return { pdfBlob, filename };
}

/**
 * Downloads a Payment Receipt PDF directly to device
 */
export async function downloadReceiptPdf(receiptElement, receipt) {
  const { pdfBlob, filename } = await generateReceiptPdfBlob(receiptElement, receipt);
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
  return { filename, size: pdfBlob.size };
}

/**
 * Generates an A4 PDF Blob for a Payment Receipt and shares it directly to WhatsApp
 */
export async function shareReceiptPdf(receiptElement, receipt, issuingName = "SREE RAM TRADERS") {
  const { pdfBlob, filename } = await generateReceiptPdfBlob(receiptElement, receipt);
  const receiptNo = receipt.paymentNo || "Receipt";
  const file = new File([pdfBlob], filename, { type: "application/pdf" });
  const shareText = `*${issuingName}*\nPayment Receipt #${receiptNo}\nAmount: ₹${Number(receipt.amount || 0).toLocaleString("en-IN")}\nThank you for your business!`;

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `Payment Receipt #${receiptNo}`,
        text: shareText
      });
      return { sharedDirectly: true };
    } catch (shareErr) {
      if (shareErr.name === "AbortError") {
        return { aborted: true };
      }
      console.warn("Native share failed, falling back to download & WhatsApp Web:", shareErr);
    }
  }

  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);

  const rawPhone = (receipt.customerPhone || receipt.partyPhone || "").replace(/\D/g, "");
  const targetPhone = rawPhone ? `91${rawPhone.slice(-10)}` : "";
  const waUrl = targetPhone
    ? `https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(shareText + "\n(Receipt PDF downloaded to your device)")}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + "\n(Receipt PDF downloaded to your device)")}`;

  window.open(waUrl, "_blank");
  return { sharedDirectly: false, downloaded: true };
}

// Page width in CSS px (96 dpi) and inner padding for each supported page size
const DOC_PAGE = {
  a4: { width: "794px", padding: "10mm" },
  a5: { width: "559px", padding: "7mm" }
};

/**
 * Generates a PDF Blob from a printable element (party ledger, purchase bill, …).
 * format: "a4" (default) or "a5" (half sheet, e.g. the compact purchase slip).
 */
export async function generateDocumentPdfBlob(docElement, filename, { format = "a4" } = {}) {
  if (!docElement) throw new Error("Printable document not found");
  const page = DOC_PAGE[format] || DOC_PAGE.a4;

  const exportContainer = document.createElement("div");
  exportContainer.style.background = "#ffffff";
  exportContainer.style.color = "#000000";
  exportContainer.style.width = page.width;
  exportContainer.style.margin = "0";
  exportContainer.style.padding = "0";
  exportContainer.style.boxSizing = "border-box";

  const clone = docElement.cloneNode(true);
  clone.style.width = "100%";
  clone.style.maxWidth = "100%";
  clone.style.maxHeight = "none";
  clone.style.overflow = "visible";
  clone.style.margin = "0";
  clone.style.padding = page.padding;
  clone.style.boxSizing = "border-box";
  clone.style.background = "#ffffff";
  clone.style.boxShadow = "none";
  clone.style.border = "none";
  exportContainer.appendChild(clone);

  const opt = {
    margin: 0,
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff", scrollX: 0, scrollY: 0 },
    jsPDF: { unit: "mm", format: DOC_PAGE[format] ? format : "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"], avoid: "tr" }
  };

  const pdfBlob = await html2pdf().set(opt).from(exportContainer).outputPdf("blob");
  return { pdfBlob, filename };
}

/** Downloads the PDF to the device. */
export async function downloadDocumentPdf(docElement, filename, options) {
  const { pdfBlob } = await generateDocumentPdfBlob(docElement, filename, options);
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
  return { filename, size: pdfBlob.size };
}

/** Opens the PDF in a new tab (print from the PDF viewer); downloads if pop-ups are blocked. */
export async function openDocumentPdf(docElement, filename, options) {
  const { pdfBlob } = await generateDocumentPdfBlob(docElement, filename, options);
  const blobUrl = URL.createObjectURL(pdfBlob);
  const win = window.open(blobUrl, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

/**
 * Shares the PDF to WhatsApp like invoices: native share sheet with the PDF on phones;
 * on desktop downloads the PDF and opens a WhatsApp chat (to the party's number when known).
 */
export async function shareDocumentPdf(docElement, filename, { title = "Document", text = "", phone = "" } = {}, options) {
  const { pdfBlob } = await generateDocumentPdfBlob(docElement, filename, options);
  const file = new File([pdfBlob], filename, { type: "application/pdf" });

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return { sharedDirectly: true };
    } catch (shareErr) {
      if (shareErr.name === "AbortError") return { aborted: true };
      console.warn("Native share failed, falling back to download:", shareErr);
    }
  }

  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);

  const rawPhone = String(phone || "").replace(/\D/g, "");
  const targetPhone = rawPhone ? `91${rawPhone.slice(-10)}` : "";
  const message = encodeURIComponent(`${text}\n(PDF downloaded to your device)`);
  const waUrl = targetPhone
    ? `https://api.whatsapp.com/send?phone=${targetPhone}&text=${message}`
    : `https://api.whatsapp.com/send?text=${message}`;
  window.open(waUrl, "_blank");
  return { sharedDirectly: false, downloaded: true, filename };
}
